import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Heart, Star, Clock, CalendarDays, MapPin, ArrowRight, Phone, Mail, Globe, Users, Palette, Scissors, DollarSign, Briefcase, GraduationCap, X } from "lucide-react";
import Marquee from "react-fast-marquee";

const APPLY_URL = "https://forms.gle/EYMH95LFWJXcTxA67";

// Lift Her Up images
const IMAGES = {
  sisterhood: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/6plr2zad_IMG_8370.webp",
  tshirt: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/rg53y52x_IMG_7031%20%281%29.webp",
  denimWorkshop: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/vhp2fi1s_IMG_6868.webp",
  hairWorkshop: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/e4i8memk_IMG_6851.webp",
  hairWorkshop2: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/c7rcw8pv_IMG_6851.webp",
  groupPhoto: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/3fgdpe3n_IMG_6528.webp",
  leader: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/oao3glnr_IMG_6418.JPEG",
  uscVisit: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/iyxqyhde_IMG_7536.webp",
  beachDay: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/5hf2q0l5_IMG_7589.webp",
  moneyMoves: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/ipfu112m_IMG_8432.webp",
  mockInterview: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/c167vbdj_IMG_8089.JPEG",
  graduates2026: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/94k5fu4u_b08aa0b684f9add491ef5af3d4daaf2a.webp",
  cakeDecorating: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/m2suozgh_IMG_4550%20%281%29.webp",
  cookingClass: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/o5wln2u9_IMG_9632%20%281%29.webp",
  intelMuseum: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/z1pje2lu_IMG_9685.webp",
  entrepreneurship: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/znqbi7yh_IMG_9066.webp",
  healthyRelationships: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/wrlaeeu4_IMG_9511.webp",
  menstrualHealth: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/adivdkyj_IMG_4024.webp",
  publicSpeaking: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/0s1wayxd_IMG_7715.webp",
  createYourBrand: "https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/4w171vhy_IMG_9967.webp",
};

const LiftHerUp = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    document.title = "Lift Her Up Academy | Soar Solutions Youth Center";
  }, []);

  const openLightbox = (src, alt, rotate = false, degrees = 90) => {
    setLightboxImage({ src, alt, rotate, degrees });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <div data-testid="lift-her-up-page" className="bg-white min-h-screen">
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={closeLightbox}
        >
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={lightboxImage.src} 
            alt={lightboxImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            style={lightboxImage.rotate ? { transform: `rotate(${lightboxImage.degrees}deg)` } : {}}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-soar-blue via-soar-blue/95 to-soar-blue" />
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-600 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-soar-teal/20 border border-soar-teal/40 text-soar-teal px-4 py-2 rounded-full text-sm font-bold tracking-wide">
              <Heart className="w-4 h-4" />
              Free Program
            </div>
            <div className="inline-flex items-center gap-2 bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
              <Clock className="w-4 h-4" />
              Applications Open April 2027
            </div>
          </div>

          <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.1] mb-4">
            Lift Her Up{" "}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              Academy
            </span>
          </h1>

          <p className="text-xl sm:text-2xl text-yellow-400/90 font-medium tracking-widest uppercase mb-6">
            Empower &middot; Inspire &middot; Transform
          </p>

          <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto mb-8">
            A 12-week empowerment program for young ladies ages 12-18, building confidence, 
            skills, and leadership through mentorship and hands-on experiences.
          </p>

          {/* Quick Info Cards */}
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
            <div className="card-elevated rounded-2xl p-4 text-center">
              <CalendarDays className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-soar-blue font-bold text-sm">12 Weeks</p>
              <p className="text-soar-blue/60 text-xs">Summer Program</p>
            </div>
            <div className="card-elevated rounded-2xl p-4 text-center">
              <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-soar-blue font-bold text-sm">Tue & Thu</p>
              <p className="text-soar-blue/60 text-xs">5:30 - 7:30 PM</p>
            </div>
            <div className="card-elevated rounded-2xl p-4 text-center">
              <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-soar-blue font-bold text-sm">Ages 12-18</p>
              <p className="text-soar-blue/60 text-xs">Young Ladies</p>
            </div>
          </div>
        </div>
      </section>

      {/* Scrolling Ribbon */}
      <div className="py-5 bg-[#212163]">
        <Marquee speed={50} gradient={false}>
          {["CONFIDENT", "WORTHY", "LEADER", "DREAM", "HER"].map((w) => (
            <span key={w} className="font-heading text-4xl text-[#ddb64d]/20 mx-12 tracking-wider">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* 2026 Graduates Feature */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden relative">
            <img
              src={IMAGES.graduates2026}
              alt="2026 Lift Her Up Academy Graduates"
              className="w-full h-[450px] object-cover"
              style={{ objectPosition: '50% 20%' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="w-6 h-6 text-soar-gold" />
                <span className="text-soar-gold text-sm font-semibold uppercase tracking-wider">Class of 2026</span>
              </div>
              <h3 className="font-heading text-3xl text-white">Our Graduates</h3>
              <p className="text-white/80 mt-2 max-w-xl">Young women who completed the Lift Her Up Academy, ready to take on the world.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What She'll Learn - Moved to top */}
      <section className="py-16 relative" data-testid="curriculum-section">
        <div className="absolute inset-0 bg-white" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-soar-blue mb-4">
              What She&apos;ll <span className="text-soar-teal">Learn</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-500 to-yellow-600 mx-auto rounded-full" />
          </div>

          <div className="space-y-6 text-center">
            <p className="text-soar-blue/80 text-lg leading-relaxed">
              Our curriculum is designed to nurture the whole person. She&apos;ll begin with 
              <span className="text-soar-teal font-semibold"> self-discovery and confidence building</span>, 
              learning to love herself because she is worthy. Through 
              <span className="text-soar-teal font-semibold"> personal grooming and hair care</span> sessions, 
              she&apos;ll develop habits that reflect her inner beauty outward.
            </p>

            <p className="text-soar-blue/80 text-lg leading-relaxed">
              She&apos;ll gain practical skills in <span className="text-soar-teal font-semibold">software basics and Canva project development</span>, 
              while <span className="text-soar-teal font-semibold">time management, communication, and decision-making</span> workshops 
              prepare her to navigate life with intention. Our <span className="text-soar-teal font-semibold">Money Moves Mastery</span> sessions 
              teach financial literacy that lasts a lifetime.
            </p>

            <p className="text-soar-blue/80 text-lg leading-relaxed">
              Career readiness comes through <span className="text-soar-teal font-semibold">career exploration and mock interviews</span>, 
              while <span className="text-soar-teal font-semibold">entrepreneurship training</span> helps her brand herself and 
              create her own lane. She&apos;ll learn <span className="text-soar-teal font-semibold">healthy relationship skills</span>, 
              and discover joy in the kitchen with <span className="text-soar-teal font-semibold">cooking and cake decorating</span> workshops.
            </p>

            <p className="text-soar-blue/80 text-lg leading-relaxed">
              Through <span className="text-soar-teal font-semibold">Beyond Bakersfield field trips</span>, she&apos;ll see 
              what&apos;s possible beyond her hometown, visiting college campuses and exploring new horizons. 
              The journey culminates in a <span className="text-soar-teal font-semibold">graduation ceremony</span> celebrating 
              her growth, achievements, and the young woman she&apos;s becoming.
            </p>
          </div>
        </div>
      </section>

      {/* I Am Her - Affirmation Section */}
      <section className="py-20 relative overflow-hidden" data-testid="affirmation-section">
        <div className="absolute inset-0 bg-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-yellow-500/30 to-yellow-700/10 rounded-3xl blur-lg" />
              <img
                src={IMAGES.tshirt}
                alt="I Am Her - Affirmation Shirt"
                className="relative rounded-3xl shadow-2xl w-full max-w-lg mx-auto border border-yellow-500/20"
              />
            </div>
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-4xl sm:text-5xl text-soar-blue">
                I Am <span className="text-soar-teal">Her.</span>
              </h2>
              <div className="space-y-3">
                {["Confident", "Capable", "Worthy", "Strong", "Enough", "Destined", "The Future"].map((word) => (
                  <div key={word} className="flex items-center gap-3">
                    <Heart className="w-5 h-5 text-soar-teal flex-shrink-0" />
                    <p className="text-soar-blue/80 text-lg font-medium">I am <span className="text-soar-teal font-bold uppercase">{word}</span></p>
                  </div>
                ))}
              </div>
              <p className="text-soar-blue/60 text-lg leading-relaxed pt-4">
                Every young woman who completes our program walks away knowing her worth, 
                speaking her truth, and ready to create her own future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hands-On Experiences - Scrolling Gallery */}
      <section className="py-24 bg-white overflow-hidden" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">What We Do</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-soar-blue mt-3 tracking-tight">
              Hands-On <span className="text-soar-teal">Experiences</span>
            </h2>
            <p className="text-soar-blue/60 mt-4 max-w-2xl mx-auto">
              From creative workshops to life skills training, our young ladies learn by doing.
            </p>
          </div>
        </div>

        {/* Scrolling Gallery */}
        <Marquee speed={60} gradient={false} pauseOnHover={true} className="py-4">
          <div className="flex gap-6 px-3">
            {/* Dream Jacket */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.groupPhoto, "Dream Jacket Workshop")}
            >
              <img src={IMAGES.groupPhoto} alt="Dream Jacket Workshop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Self Discovery & Confidence</span>
                <h4 className="font-heading text-lg text-white">Dream Jacket</h4>
              </div>
            </div>

            {/* Hair Care */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.hairWorkshop, "Hair Care Workshop")}
            >
              <img src={IMAGES.hairWorkshop} alt="Hair Care Workshop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Personal Grooming</span>
                <h4 className="font-heading text-lg text-white">Hair Care</h4>
              </div>
            </div>

            {/* Money Moves - using mockInterview image */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.mockInterview, "Money Moves Mastery")}
            >
              <img src={IMAGES.mockInterview} alt="Financial Literacy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Financial Literacy</span>
                <h4 className="font-heading text-lg text-white">Money Moves Mastery</h4>
              </div>
            </div>

            {/* Mock Interviews - using moneyMoves image */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.moneyMoves, "Mock Interviews")}
            >
              <img src={IMAGES.moneyMoves} alt="Mock Interviews" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Career Prep</span>
                <h4 className="font-heading text-lg text-white">Mock Interviews</h4>
              </div>
            </div>

            {/* USC Visit */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.uscVisit, "USC University")}
            >
              <img src={IMAGES.uscVisit} alt="USC Campus Visit" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Beyond Bakersfield</span>
                <h4 className="font-heading text-lg text-white">USC University</h4>
              </div>
            </div>

            {/* Beach Day */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.beachDay, "Santa Monica Beach")}
            >
              <img src={IMAGES.beachDay} alt="Beach Day" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Beyond Bakersfield</span>
                <h4 className="font-heading text-lg text-white">Santa Monica Beach</h4>
              </div>
            </div>

            {/* Cake Decorating */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.cakeDecorating, "Cake Decorating")}
            >
              <img src={IMAGES.cakeDecorating} alt="Cake Decorating" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Baking Into Her Future</span>
                <h4 className="font-heading text-lg text-white">Cake Decorating</h4>
              </div>
            </div>

            {/* Cooking Class */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.cookingClass, "Cooking Class", true)}
            >
              <img src={IMAGES.cookingClass} alt="Cooking Class" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ transform: 'rotate(90deg) scale(1.4)' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Chef&apos;s Kiss</span>
                <h4 className="font-heading text-lg text-white">Cooking Class</h4>
              </div>
            </div>

            {/* Intel Museum */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.intelMuseum, "Intel Museum")}
            >
              <img src={IMAGES.intelMuseum} alt="Intel Museum Visit" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Beyond Bakersfield</span>
                <h4 className="font-heading text-lg text-white">Intel Museum</h4>
              </div>
            </div>

            {/* Entrepreneurship */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.entrepreneurship, "Entrepreneurship Workshop", true)}
            >
              <img src={IMAGES.entrepreneurship} alt="Entrepreneurship Workshop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ transform: 'rotate(90deg) scale(1.4)' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Entrepreneurship</span>
                <h4 className="font-heading text-lg text-white">Branding Yourself</h4>
              </div>
            </div>

            {/* Healthy Relationships */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.healthyRelationships, "Healthy Relationships")}
            >
              <img src={IMAGES.healthyRelationships} alt="Healthy Relationships Workshop" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Healthy Relationships</span>
                <h4 className="font-heading text-lg text-white">Self-Expression</h4>
              </div>
            </div>

            {/* Menstrual Health */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.menstrualHealth, "Menstrual Health")}
            >
              <img src={IMAGES.menstrualHealth} alt="Menstrual Health" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Health & Hygiene</span>
                <h4 className="font-heading text-lg text-white">Menstrual Health</h4>
              </div>
            </div>

            {/* Public Speaking */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.publicSpeaking, "Public Speaking", true)}
            >
              <img src={IMAGES.publicSpeaking} alt="Public Speaking" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ transform: 'rotate(90deg) scale(1.4)' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Public Speaking</span>
                <h4 className="font-heading text-lg text-white">Find Your Voice</h4>
              </div>
            </div>

            {/* Create Your Brand */}
            <div 
              className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-64 cursor-pointer"
              onClick={() => openLightbox(IMAGES.createYourBrand, "Create Your Brand", true)}
            >
              <img src={IMAGES.createYourBrand} alt="Create Your Brand" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ transform: 'rotate(90deg) scale(1.4)' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-soar-gold text-xs font-semibold uppercase">Branding</span>
                <h4 className="font-heading text-lg text-white">Create Your Brand</h4>
              </div>
            </div>
          </div>
        </Marquee>
      </section>

      {/* 2026 Presenters - Ladies who poured into our young ladies */}
      <section className="py-16 bg-gradient-to-b from-white to-soar-teal/5 overflow-hidden" data-testid="presenters-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">Thank You</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-soar-blue mt-3 tracking-tight">
              2026 <span className="text-soar-teal">Presenters</span>
            </h2>
            <p className="text-soar-blue/60 mt-4 max-w-2xl mx-auto">
              The amazing women who poured into our young ladies this year.
            </p>
          </div>
        </div>

        {/* Presenters Scrolling Gallery */}
        <Marquee speed={60} gradient={false} pauseOnHover={true} pauseOnClick={true} direction="right" className="py-4">
            <div className="flex gap-6 px-3">
              {/* Cameron Reid */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/1v6c1m1m_03EDB99C-9E4E-41A0-B26B-0755212B9073-1.png", "Cameron Reid")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/1v6c1m1m_03EDB99C-9E4E-41A0-B26B-0755212B9073-1.png" alt="Cameron Reid" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Cameron Reid</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Kelsey Jumique */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/8p0v8jor_IMG_7904%20%281%29.PNG", "Kelsey Jumique")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/8p0v8jor_IMG_7904%20%281%29.PNG" alt="Kelsey Jumique" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Kelsey Jumique</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Stephanie Spotwood */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/e82gw9i0_IMG_6354.webp", "Stephanie Spotwood")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/e82gw9i0_IMG_6354.webp" alt="Stephanie Spotwood" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Stephanie Spotwood</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Juanita Caudle */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer bg-gray-400"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/6toyz3mo_4eb88b6fe03819adf605d4d6271651d9.webp", "Juanita Caudle")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/6toyz3mo_4eb88b6fe03819adf605d4d6271651d9.webp" alt="Juanita Caudle" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" style={{ objectPosition: '30% 25%', transform: 'scale(1.6)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-600/90 via-gray-500/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Juanita Caudle</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Yoshi Rogers */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/5i0sazrx_Yoshi%20.jpeg", "Yoshi Rogers")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/5i0sazrx_Yoshi%20.jpeg" alt="Yoshi Rogers" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Yoshi Rogers</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Brushay Wandick */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/nl5nquqp_shay.jpg", "Brushay Wandick")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/nl5nquqp_shay.jpg" alt="Brushay Wandick" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Brushay Wandick</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Carrie Blind */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/6tzcw27t_Carrie%20Blind%20__%20F%20a%20M%20I%20L%20Y.webp", "Carrie Blind")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/6tzcw27t_Carrie%20Blind%20__%20F%20a%20M%20I%20L%20Y.webp" alt="Carrie Blind" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" style={{ objectPosition: '50% 30%', transform: 'scale(3.5)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Carrie Blind</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Shontay Smith */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/rgdv0hgl_IMG_2196.webp", "Shontay Smith")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/rgdv0hgl_IMG_2196.webp" alt="Shontay Smith" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" style={{ objectPosition: '50% 15%', transform: 'scale(1.5)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Shontay Smith</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Eliska Anderson */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/iw9fha5d_Screenshot%20%282%29.jpg", "Eliska Anderson")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/iw9fha5d_Screenshot%20%282%29.jpg" alt="Eliska Anderson" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" style={{ objectPosition: '50% 10%', transform: 'scale(3.5)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Eliska Anderson</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Emily Phillips */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/mxb1gp37_2024%20headshot%201%20%281%29.jpg", "Emily Phillips")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/mxb1gp37_2024%20headshot%201%20%281%29.jpg" alt="Emily Phillips" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Emily Phillips</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Michelle Chavez Richard */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/tio3x5pa_Screenshot%20%281%29.jpg", "Michelle Chavez Richard")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/tio3x5pa_Screenshot%20%281%29.jpg" alt="Michelle Chavez Richard" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Michelle Chavez Richard</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Dess Perkins */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/w951x9yz_Headshot%202.jpg", "Dess Perkins")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/w951x9yz_Headshot%202.jpg" alt="Dess Perkins" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Dess Perkins</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Chef Ashley Jones */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/1v6dqruk_Screenshot.jpg", "Chef Ashley Jones")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/1v6dqruk_Screenshot.jpg" alt="Chef Ashley Jones" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Chef Ashley Jones</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Regina "Kake" Walker */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/xty7dy7m_IMG_5186.webp", "Regina Kake Walker")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/xty7dy7m_IMG_5186.webp" alt="Regina Kake Walker" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Regina "Kake" Walker</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>

              {/* Shar Cole */}
              <div 
                className="group relative rounded-2xl overflow-hidden flex-shrink-0 w-72 h-80 cursor-pointer"
                onClick={() => openLightbox("https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/ar7mwhsm_image0.jpg", "Shar Cole")}
              >
                <img src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/ar7mwhsm_image0.jpg" alt="Shar Cole" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/90 via-soar-blue/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h4 className="font-heading text-xl text-white">Shar Cole</h4>
                  <span className="text-soar-gold text-sm font-semibold">Presenter</span>
                </div>
              </div>
            </div>
          </Marquee>
      </section>

      {/* Group Photo Banner */}
      <section className="py-16 relative overflow-hidden bg-white" data-testid="group-banner">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src={IMAGES.sisterhood}
              alt="Lift Her Up Academy Participants"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
              <h3 className="font-heading text-3xl text-white mb-2">Building a Sisterhood</h3>
              <p className="text-white/80 max-w-2xl mx-auto">
                More than a program, it&apos;s a community of young women supporting each other, 
                growing together, and lifting each other up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet The Leader */}
      <section className="py-20 relative overflow-hidden" data-testid="leader-section">
        <div className="absolute inset-0 bg-white" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <span className="text-soar-teal text-sm font-semibold uppercase tracking-widest">Building Confidence. Creating Futures.</span>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl text-soar-blue">
                Led With <span className="text-soar-teal">Heart</span>
              </h2>
              <p className="text-soar-blue/70 text-lg leading-relaxed">
                Our program is led by passionate mentors who are dedicated to helping young women 
                discover their potential. With hands-on guidance and real-world experience, 
                we&apos;re creating the next generation of confident leaders.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="card-elevated rounded-2xl p-5 text-center">
                  <p className="font-heading text-3xl text-soar-teal">24</p>
                  <p className="text-soar-blue/60 text-sm">Sessions</p>
                </div>
                <div className="card-elevated rounded-2xl p-5 text-center">
                  <p className="font-heading text-3xl text-soar-teal">12</p>
                  <p className="text-soar-blue/60 text-sm">Weeks</p>
                </div>
              </div>
            </div>
            <div className="relative order-1 lg:order-2">
              <div className="absolute -inset-2 bg-gradient-to-br from-yellow-500/30 to-yellow-700/10 rounded-3xl blur-lg" />
              <img
                src={IMAGES.leader}
                alt="Lift Her Up Academy Leader"
                className="relative rounded-3xl shadow-2xl w-full max-w-md mx-auto lg:ml-auto border border-yellow-500/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 text-[#0a0a2e]/80 mx-auto mb-6" />
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0a0a2e] mb-4">
            Young Ladies Ages 12-18
          </h2>
          <p className="text-[#0a0a2e]/70 text-lg mb-4 max-w-2xl mx-auto">
            This is her moment to discover who she is, build real-world skills, and step 
            into her future with confidence.
          </p>
          <p className="text-[#0a0a2e] font-bold text-xl mb-8">
            Applications open April 2027
          </p>
        </div>
      </section>

      {/* Sponsor & Contact */}
      <section className="py-16 bg-white" data-testid="sponsor-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sponsor */}
          <div className="text-center mb-12">
            <p className="text-soar-teal/70 uppercase tracking-widest text-sm font-semibold mb-3">Thank You to Our Sponsors</p>
            <p className="text-soar-blue text-xl font-heading font-bold">The Women&apos;s and Girls&apos; Fund</p>
          </div>

          {/* Contact Info */}
          <div className="grid sm:grid-cols-3 gap-6">
            <a href="https://www.soar-solutions.org" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 card-elevated rounded-2xl p-5 hover:bg-soar-teal/10 hover:border-soar-teal/30 transition-all" data-testid="contact-website">
              <Globe className="w-5 h-5 text-soar-teal" />
              <span className="text-soar-blue/80 text-sm">www.Soar-Solutions.org</span>
            </a>
            <a href="tel:661-331-4767" className="flex items-center justify-center gap-3 card-elevated rounded-2xl p-5 hover:bg-soar-teal/10 hover:border-soar-teal/30 transition-all" data-testid="contact-phone">
              <Phone className="w-5 h-5 text-soar-teal" />
              <span className="text-soar-blue/80 text-sm">661-331-4767</span>
            </a>
            <a href="mailto:LiftHerUp@Soar-Solutions.org" className="flex items-center justify-center gap-3 card-elevated rounded-2xl p-5 hover:bg-soar-teal/10 hover:border-soar-teal/30 transition-all" data-testid="contact-email">
              <Mail className="w-5 h-5 text-soar-teal" />
              <span className="text-soar-blue/80 text-sm">LiftHerUp@Soar-Solutions.org</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LiftHerUp;
